import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { getDashboard } from "@/modules/Dashboard/service/DashboardService"
import { DashboardResponseList, Indicador } from "@/types/dashboard"
import { useEffect, useState } from "react"
import PessoasEmpresaChart from "@/modules/Dashboard/components/PessoasEmpresaChart"
import ParticipacoesEventosChart from "@/modules/Dashboard/components/ParticipacoesEventosChart"
import ParticipacoesPorTipoChart from "@/modules/Dashboard/components/ParticipacoesPorTipoChart"
import { Skeleton } from "@/components/ui/skeleton"
import { useLoading } from "@/hooks/useLoading"
import { useAuth } from "@/modules/Auth/context/AuthContext"
import { EventoDataType } from "@/types/events"
import { getDate } from "@/utils/formatDate"
import { ptBR } from "date-fns/locale"
import { getCalendarioAction } from "@/modules/Events/service/EventsService"
import { Icon } from "@/components/Atoms/Icon"
import {
  getPendingFeedback,
  mentoringRate,
  PendingMentoringsRateResponse,
} from "@/services/mentoriasService"
import { MentoringFeedbackModal } from "@/modules/Mentorings/components/MentoringFeedbackModal"
import { useToast } from "@/contexts/ToastContext"
import { Avatar, AvatarFallback, AvatarImage } from "@radix-ui/react-avatar"
import { format, parseISO } from "date-fns"
import { PageHeader } from "@/components/Molecules/PageHeader/PageHeader"
import { Button } from "@/components/ui/button"
import { getFaceImage } from "@/modules/Users/service/UsersService"
import { useNavigate } from "react-router-dom"

const isRNWebView = typeof window !== "undefined" && !!(window as any).ReactNativeWebView

export default function DashboardPage() {
  const [data, setData] = useState<DashboardResponseList | null>(null)
  const [loading, setLoading] = useState(true)
  const toast = useToast()

  const [nextEvent, setNextEvent] = useState<EventoDataType>()
  const [error, setError] = useState<string | null>(null)
  const { showLoading, hideLoading } = useLoading()
  const [isMentoringFeedbackModalOpen, setIsMentoringFeedbackModalOpen] = useState(false)
  const [pendingMentoring, setPendingMentoring] = useState<PendingMentoringsRateResponse>()
  const [hasFaceCapture, setHasFaceCapture] = useState<boolean>(false)
  const [showFaceIdModal, setShowFaceIdModal] = useState(false)
  const { user, logout } = useAuth()
  const navigate = useNavigate()

  useEffect(() => {
    if (!isRNWebView) return
    if (sessionStorage.getItem("showFaceIdPrompt") && !localStorage.getItem("faceIdRefreshToken")) {
      sessionStorage.removeItem("showFaceIdPrompt")
      setShowFaceIdModal(true)
    }
  }, [])

  const handleEnableFaceId = () => {
    const rt = localStorage.getItem("refreshToken")
    const userStr = localStorage.getItem("user")
    if (rt && userStr) {
      localStorage.setItem("faceIdRefreshToken", rt)
      localStorage.setItem("faceIdUser", userStr)
      toast.success("Face ID habilitado! Na próxima abertura do app, você entrará automaticamente.")
    }
    setShowFaceIdModal(false)
  }

  const handleSkipFaceId = () => {
    setShowFaceIdModal(false)
  }

  const handleGetPendingFeedback = () => {
    getPendingFeedback(user.empId)
      .then((response) => {
        console.log(response)
      })
      .catch((error) => {
        console.error(error)
      })
  }

  const handleMentoringFeedbackSubmit = (rating: number, comments: string) => {
    if (!pendingMentoring) return

    showLoading()
    mentoringRate({
      mentoria_id: pendingMentoring.mentoria_id,
      emp_id: user.empId,
      mentorado_id: user.pesId,
      mentorado_avaliacao: rating,
      mentorado_observacao: comments,
    })
      .then(() => {
        toast.success("Avaliação enviada com sucesso!")
        setIsMentoringFeedbackModalOpen(false)
        setPendingMentoring(user.empId)
      })
      .catch(() => {
        toast.danger("Erro ao enviar avaliação.")
      })
      .finally(() => {
        hideLoading()
      })
  }

  useEffect(() => {
    getFaceImage()
      .then(({ data }) => {
        setHasFaceCapture(data.data.data.hasPhoto)
      })
      .catch(() => {
        setHasFaceCapture(false)
      })
  }, [])

  useEffect(() => {
    showLoading()

    if (!user.pesId || !user.empId) {
      logout()
      window.location.href = "/"
    }

    getDashboard(user.empId, { pes_id: String(user.pesId) })
      .then(({ data }) => {
        setData(data)
        setLoading(false)
      })
      .catch((error) => {
        setError("Erro ao carregar os dados do dashboard.")
        setLoading(false)
        console.error(error)
      })
      .finally(() => {
        hideLoading()
      })

    const promises = [
      getCalendarioAction()
        .then(({ data }) => {
          const dataAtual = new Date()

          const eventosFuturosDisponiveis = data.data.filter((evento) => {
            const dataInicioEvento = new Date(evento.data_inicio)
            return dataInicioEvento >= dataAtual && evento.inscrito === false
          })

          const proximoEvento =
            eventosFuturosDisponiveis.length > 0
              ? eventosFuturosDisponiveis.reduce((maisProximo, eventoAtual) => {
                  const dataMaisProximo = new Date(maisProximo.data_inicio)
                  const dataEventoAtual = new Date(eventoAtual.data_inicio)

                  return dataEventoAtual < dataMaisProximo ? eventoAtual : maisProximo
                }, eventosFuturosDisponiveis[0])
              : null

          setNextEvent(proximoEvento)
        })
        .catch((error) => console.log("Erro ao buscar calendário:", error)),

      handleGetPendingFeedback(),
    ]

    Promise.allSettled(promises).then(() => {
      hideLoading()
    })
  }, [])

  const getIndicador = (id: Indicador["id"]) => {
    return data?.[0].resultado_final.indicadores.find((ind) => ind.id === id)?.total
  }

  const pessoasAtivas = getIndicador("qtd_pessoas_ativas")
  const participacaoEventos = getIndicador("qtd_pessoas_eventos")
  const aprovacoesPendentes = getIndicador("qtd_eventos_pessoas_pendentes")

  const pessoasEmpresa = data?.[0].resultado_final.pessoas_empresa
  const participacoesPerfil = data?.[0].resultado_final.eventos_empresa_perfil
  const participacoesTipo = data?.[0].resultado_final.eventos_empresa_tipo

  if (loading) {
    return (
      <div className="animate-fade-in space-y-6">
        <div className="grid gap-4 md:grid-cols-3">
          <Skeleton className="h-32" />
          <Skeleton className="h-32" />
          <Skeleton className="h-32" />
        </div>
        <div className="grid gap-4 md:grid-cols-2">
          <Skeleton className="h-64" />
          <Skeleton className="h-64" />
        </div>
        <div>
          <Skeleton className="h-80" />
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex h-full items-center justify-center">
        <p className="text-red-500">{error}</p>
      </div>
    )
  }

  const getPeriodText = (startDate: string, endDate: string) => {
    const start = format(parseISO(startDate), "dd")
    const end = format(parseISO(endDate), "dd")
    if (start === end) {
      return `${start} de`
    }

    return `${start} à ${end}`
  }

  return (
    <div>
      <PageHeader
        title="Dashboard"
        icon="chart-pie-alt"
      />
      <div className="flex animate-fade-in flex-wrap gap-y-4 px-6 lg:gap-x-4">
        <div className="card-premium flex h-full flex-[1] animate-fade-in flex-wrap gap-4 rounded-lg bg-accent/20 p-4 lg:order-1">
          <section className="flex flex-1 basis-[200px] flex-col items-center justify-center text-center">
            <div className="flex size-24 items-center justify-center overflow-hidden rounded-full border border-orange-500">
              <Avatar className="">
                <AvatarImage
                  src={user.pesFotoUrl}
                  alt={user.pesNome}
                />
                <AvatarFallback>
                  {user.pesNome
                    ?.split(" ")
                    .map((n) => n[0])
                    .join("")}
                </AvatarFallback>
              </Avatar>
            </div>
            <div className="flex flex-col items-center gap-2">
              <h6 className="text-lg font-bold">{user.pesNome}</h6>
              <p className="text-sm italic text-[#777]">{user.pesEmail}</p>
              {(user.pesTipo === 3 || user.pesTipo === 5) && (
                <button
                  onClick={() => navigate("/face-capture")}
                  className="mt-1 flex items-center gap-2 rounded-xl bg-gradient-to-r from-orange-500 to-orange-600 px-4 py-2 text-xs font-semibold text-white shadow-md shadow-orange-500/30 transition-all duration-200 hover:scale-105 hover:shadow-lg hover:shadow-orange-500/40 active:scale-95"
                >
                  <Icon name="face-viewfinder" />
                  {hasFaceCapture ? "Atualizar Biometria Facial" : "Cadastrar Biometria Facial"}
                </button>
              )}
            </div>
          </section>

          {/* <hr /> */}

          <div className="card-premium flex flex-1 basis-[250px] flex-col space-y-2 self-start bg-gradient-to-tr from-transparent via-transparent to-green-900/50 p-3">
            <div className="flex items-center gap-4">
              <div className="flex size-10 items-center justify-center rounded-full bg-accent/20">
                <Icon name="badget-check-alt" />
              </div>
              <div>
                <h6>Eventos que participei</h6>
              </div>
            </div>
            <div className="flex flex-col">
              {user.pesTipo !== 2 ||
                (user.pesAdmin && (
                  <>
                    <div className="flex items-center justify-between">
                      <span className="text-xs">Encontros Giants</span>
                      <h6 className="text-lg font-bold">
                        {data[0].resultado_final.participacoes_cliente.encontros}
                      </h6>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-xs">Presenciais</span>
                      <h6 className="text-lg font-bold">
                        {data[0].resultado_final.participacoes_cliente.presenciais}
                      </h6>
                    </div>
                  </>
                ))}
              <div className="flex items-center justify-between">
                <span className="text-xs">Onlives</span>
                <h6 className="text-lg font-bold">
                  {data[0].resultado_final.participacoes_cliente.onlives}
                </h6>
              </div>
            </div>
          </div>

          <div className="card-premium flex flex-1 basis-[250px] space-y-2 self-start bg-gradient-to-tr from-transparent via-transparent to-orange-900/50 p-3">
            <div className="flex items-center gap-4">
              <div className="flex size-10 items-center justify-center rounded-full bg-accent/20">
                <Icon name="pending" />
              </div>
              <div>
                <h6>Inscrições pendentes</h6>
                <p className="text-3xl font-bold text-foreground">
                  {data[0].resultado_final.participacoes_cliente.pendentes}
                </p>
              </div>
            </div>
          </div>

          {nextEvent && (
            <div className="card-premium flex flex-auto flex-wrap justify-between gap-4 p-4">
              <div className="flex flex-col gap-3">
                <div className="flex size-10 items-center justify-center rounded-full bg-primary/10">
                  <Icon name="calendar" />
                </div>
                <div>
                  <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    Próximo Evento
                  </p>
                  <h2 className="text-lg font-semibold text-foreground">{nextEvent.descricao}</h2>
                </div>
              </div>

              <hr />

              <div className="flex flex-1 items-center justify-around gap-4">
                <div className="flex justify-end gap-2 text-muted-foreground">
                  <div className="flex flex-col items-end">
                    <div className="text-nowrap text-2xl">
                      <span>{getPeriodText(nextEvent.data_inicio, nextEvent.data_fim)}</span>
                    </div>
                    <div>
                      <span className="text-4xl">
                        {format(parseISO(nextEvent.data_inicio), "MMM", {
                          locale: ptBR,
                        }).toUpperCase()}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="flex flex-wrap items-start gap-2 text-muted-foreground">
                  <Icon name="marker" />
                  <span className="text-sm">{nextEvent.endereco}</span>
                </div>
              </div>

              <div className="flex flex-wrap gap-2 self-center rounded-lg bg-red-950/40 px-3 py-2 text-red-500">
                <p className="">Inscrições até:</p>
                <div>{getDate(nextEvent.data_limite)}</div>
              </div>
            </div>
          )}

          {pendingMentoring && (
            <MentoringFeedbackModal
              isOpen={isMentoringFeedbackModalOpen}
              onClose={() => setIsMentoringFeedbackModalOpen(false)}
              mentoring={pendingMentoring}
              onSubmit={handleMentoringFeedbackSubmit}
            />
          )}
        </div>

        {user.pesTipo !== 2 && (
          <section className="card-premium flex flex-[3] flex-wrap gap-4 p-4">
            <div className="flex flex-wrap items-center gap-2">
              <div className="flex flex-1 justify-center">
                <div className="flex size-[70px] items-center justify-center rounded-full border bg-muted/20">
                  <Icon name="building" />
                </div>
              </div>
              <div className="flex flex-col">
                <h3 className="text-2xl font-bold">{user.empFantasia}</h3>
                <h3 className="text-lg">{user.catp_descricao}</h3>
              </div>
            </div>
            <div className="grid w-full grid-cols-1 gap-4 lg:grid-cols-3">
              <Card>
                <CardHeader>
                  <CardTitle className="tracking-normal">Pessoas Ativas</CardTitle>
                  <CardDescription>Qtd cadastrados</CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-3xl font-bold">{pessoasAtivas}</p>
                </CardContent>
              </Card>
              <Card>
                <CardHeader>
                  <CardTitle className="tracking-normal">Participação em Eventos</CardTitle>
                  <CardDescription>Qtd eventos</CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-3xl font-bold">{participacaoEventos}</p>
                </CardContent>
              </Card>
              <Card>
                <CardHeader>
                  <CardTitle className="tracking-normal">Aprovações Pendentes</CardTitle>
                  <CardDescription>Qtd eventos</CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-3xl font-bold">{aprovacoesPendentes}</p>
                </CardContent>
              </Card>
            </div>

            <div className="flex flex-1 flex-wrap gap-4">
              {pessoasEmpresa && <PessoasEmpresaChart data={pessoasEmpresa} />}
              {participacoesPerfil && <ParticipacoesEventosChart data={participacoesPerfil} />}
              {participacoesTipo && <ParticipacoesPorTipoChart data={participacoesTipo} />}
            </div>
          </section>
        )}
      </div>

      {showFaceIdModal && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/60 p-4 sm:items-center">
          <div className="w-full max-w-sm space-y-6 rounded-2xl bg-zinc-900 p-8">
            <div className="flex flex-col items-center gap-3 text-center">
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
                <Icon name="fingerprint" size={32} color="primary" />
              </div>
              <h2 className="text-xl font-semibold text-foreground">Habilitar Face ID</h2>
              <p className="text-sm text-muted-foreground">
                Nas próximas vezes que abrir o app, você entrará automaticamente com o Face ID, sem precisar digitar sua senha.
              </p>
            </div>
            <div className="flex flex-col gap-3">
              <Button
                onClick={handleEnableFaceId}
                className="w-full"
              >
                Habilitar Face ID
              </Button>
              <button
                onClick={handleSkipFaceId}
                className="w-full py-3 text-sm text-muted-foreground"
              >
                Agora não
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
